import React, { useState, useEffect, useRef } from "react";
import { useWebSocket } from "./WebSocketContext";
import { 
  Box, 
  Button, 
  TextField, 
  FormControlLabel, 
  Switch, 
  Typography, 
  Tooltip, 
  IconButton,
  Paper,
  Badge,
  CircularProgress,
  Fade,
  Grow,
  useTheme,
  useMediaQuery
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import EmailIcon from '@mui/icons-material/Email';
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTranslation } from "react-i18next";
import settingOutlookConnectorIcon from '../images/outlook.png'; // Import the custom icon
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import CloseButton from './Common/CloseButton';

dayjs.extend(utc);

// Define theme colors for consistent styling
const themeColors = {
  primary: '#5757b0',
  primaryLight: '#7878c5',
  primaryDark: '#3a3a8c',
  accent: '#ff8a65',
  text: '#333',
  lightText: '#f5f5f5',
  background: '#f8f9fa',
  cardBackground: '#ffffff'
};

// Reusable styled components
const StyledTextField = ({ disabled, ...props }) => (
  <TextField
    disabled={disabled}
    variant="outlined"
    fullWidth
    sx={{
      '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
          borderColor: themeColors.primary,
        },
      },
      '& label.Mui-focused': {
        color: themeColors.primary,
      },
      transition: 'all 0.3s ease',
      mb: 3
    }}
    {...props}
  />
);

const StyledPaper = ({ children, ...props }) => (
  <Paper
    elevation={0}
    sx={{
      p: props.condensed ? 2 : 3,
      mb: 3,
      borderRadius: 2,
      background: props.condensed ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)',
      ...(props.withBorder && { border: '1px solid rgba(200, 200, 200, 0.3)' }),
      backdropFilter: props.withBlur ? 'blur(10px)' : 'none',
      ...props.sx
    }}
  >
    {children}
  </Paper>
);

const ActionButton = ({ variant, icon, label, onClick, disabled, loading, success, sx }) => (
  <Button
    variant={variant}
    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : icon}
    onClick={onClick}
    disabled={disabled || loading}
    sx={{
      ...(variant === 'contained' ? {
        backgroundColor: themeColors.primary,
        color: themeColors.lightText,
        '&:hover': { backgroundColor: themeColors.primaryLight },
        '&.Mui-disabled': { 
          backgroundColor: 'rgba(87, 87, 176, 0.4)', 
          color: 'rgba(255, 255, 255, 0.6)' 
        },
        boxShadow: '0 4px 10px rgba(87, 87, 176, 0.3)',
        '&:not(.Mui-disabled):hover': { 
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 12px rgba(87, 87, 176, 0.4)',
        },
      } : {
        color: themeColors.primary,
        borderColor: themeColors.primary,
        '&:hover': { 
          borderColor: themeColors.primaryLight,
          backgroundColor: 'rgba(87, 87, 176, 0.04)'
        },
      }),
      borderRadius: 2,
      px: 3,
      transition: 'all 0.3s ease',
      ...sx
    }}
  >
    {success ? success : label}
  </Button>
);

const OutlookApiConnector = ({ caseid, forceOpen = false, onOpen }) => {
    const { t } = useTranslation();
    const { ws, subscribeToMessages } = useWebSocket();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Calculate responsive sizes
    const iconSize = isMobile ? 32 : isTablet ? 38 : 42;
    const containerPadding = isMobile ? '6px' : '8px';
    const progressSize = isMobile ? 44 : isTablet ? 52 : 58;
    const progressOffset = isMobile ? '-6px' : '-8px';
    const captionFontSize = isMobile ? '0.7rem' : '0.75rem';

    const [outlookApiEnabled, setOutlookApiEnabled] = useState(false);
    const [emails, setEmails] = useState("");
    const [folders, setFolders] = useState("");
    const [account, setAccount] = useState("");
    const [searchTerms, setSearchTerms] = useState("");
    const [outlookLastUpdate, setOutlookLastUpdate] = useState(dayjs().subtract(6, "month"));
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // To store the initial settings once they're loaded
    const initialOutlookSettings = useRef(null);

    useEffect(() => {
        if (ws) {
            setLoading(true);
            ws.send(JSON.stringify({ command: "getOutlookApiSettings", caseid }));
        }
    }, [ws, caseid]);

    // Auto-open modal if forceOpen prop is true
    useEffect(() => {
        if (forceOpen && !open) {
            setOpen(true);
            if (onOpen) {
                onOpen();
            }
        }
    }, [forceOpen, open, onOpen]);

    useEffect(() => {
        if (initialOutlookSettings.current) {
          // Compare current settings to initial settings to determine if any changes were made
          const isFormUnchanged =
            outlookApiEnabled === initialOutlookSettings.current.enable &&
            emails === initialOutlookSettings.current.emails &&
            folders === initialOutlookSettings.current.folders &&
            account === initialOutlookSettings.current.account &&
            searchTerms === initialOutlookSettings.current.searchterms &&
            outlookLastUpdate.isSame(initialOutlookSettings.current.last_update);
    
          setIsSaveDisabled(isFormUnchanged || !outlookApiEnabled);
        }
      }, [outlookApiEnabled, emails, folders, account, searchTerms, outlookLastUpdate]);

    useEffect(() => {
        const unsubscribe = subscribeToMessages((parsedMessage) => {
            try {
                // Message is already parsed in WebSocketContext
                if (parsedMessage.command === "getOutlookApiSettings") {
                    console.log("Received getOutlookApiSettings:", parsedMessage);
                    const { enable, emails, account, searchTerms, verified, lastUpdate, folders } = parsedMessage.data;
                    const loadedSettings = {
                        enable: enable === 1,
                        emails: emails || "",
                        folders: folders || "",
                        account: account || "",
                        searchterms: searchTerms || "",
                        verified: verified || false,
                        last_update: lastUpdate ? dayjs.utc(lastUpdate).local() : dayjs().subtract(6, "month"),
                    };

                    setOutlookApiEnabled(loadedSettings.enable);
                    setIsEmailVerified(loadedSettings.verified);
                    setEmails(loadedSettings.emails);
                    setFolders(loadedSettings.folders);
                    setAccount(loadedSettings.account);
                    setSearchTerms(loadedSettings.searchterms);
                    setOutlookLastUpdate(loadedSettings.last_update);

                    // Store the initial settings for later comparison
                    initialOutlookSettings.current = loadedSettings;

                    if (loadedSettings.emails || loadedSettings.searchterms || loadedSettings.folders) {
                        // Disable save button after settings load
                        setIsSaveDisabled(true);
                    } else {
                        // both emails to search and search terms are empty, meaning the user's email is a new one and was just verified,
                        // let the user enter what they want to search for
                        setIsSaveDisabled(false);
                    }
                    setLoading(false);
                }
            } catch (error) {
                console.error("Error parsing WebSocket message:", error);
                setLoading(false);
            }
        });

        return unsubscribe;
    }, [subscribeToMessages]);

    useEffect(() => {
        const unsubscribe = subscribeToMessages((parsedMessage) => {
            
            if (parsedMessage.command === "authorizeOutlookApiStart") {
                console.log("Received authorization URL:", parsedMessage.url);
                const { url } = parsedMessage;
                if (url) {
                    // Redirect user to the authorization URL
                    window.location.href = url;
                } else {
                    console.error("Authorization URL missing in the response.");
                }
            }
        });

        // Cleanup on unmount
        return () => {
            unsubscribe();
        };
    }, [subscribeToMessages]);

    useEffect(() => {
        const unsubscribe = subscribeToMessages((parsedMessage) => {
            if (parsedMessage.command === "saveOutlookApiSettings") {
                console.log("Save outlook API result:", parsedMessage);
                const { result } = parsedMessage;
                if (result) {
                    setSaveSuccess(true);
                    setIsSaveDisabled(true); // Disable save button after saving
                    
                    // Reset success message after 2 seconds
                    setTimeout(() => {
                        setSaveSuccess(false);
                    }, 2000);
                } else {
                    // alert(t("outlook_api_connector.settings_saved_error"));
                    setIsSaveDisabled(false);
                }
                setLoading(false);
            }
        });

        // Cleanup on unmount
        return () => {
            unsubscribe();
        };
    }, [subscribeToMessages, t])

    const handleOutlookApiToggle = (enabled) => {
        setOutlookApiEnabled(enabled);
        setIsSaveDisabled(false);
        if (ws) {
            ws.send(JSON.stringify({ command: "enableOutlookApi", enable: enabled ? "1" : "0", caseid }));
        }
    };

    const handleVerifyEmail = () => {
        setLoading(true);
        if (ws) {
            ws.send(JSON.stringify({
                command: "authorizeOutlookApi",
                userEmail: account,
                caseid,
                returnUrl: window.location.href
            }));
        }
    }

    const handleSaveOutlookApiSettings = () => {
        setLoading(true);
        if (ws) {
            ws.send(
                JSON.stringify({
                    command: "saveOutlookApiSettings",
                    caseid,
                    account,
                    emails,
                    folders,
                    searchTerms,
                    lastUpdate: outlookLastUpdate ? outlookLastUpdate.utc().toISOString() : "",
                })
            );
        }
        
        // Store initial settings after save
        initialOutlookSettings.current = { 
            enable: outlookApiEnabled, 
            emails, 
            folders,
            account, 
            searchterms: searchTerms, 
            last_update: outlookLastUpdate 
        };
    };
    
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    
    const handleFieldChange = (setter) => (e) => {
        setter(e.target.value);
        setIsSaveDisabled(false);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box 
                p={1} 
                width={{ xs: '100%', sm: 130, md: 150 }}
                sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-5px)',
                    }
                }}
            >
                <Box 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center"
                >
                    <Tooltip 
                        title={t("outlook_api_connector.title")}
                        arrow
                        placement="top"
                    >
                        <Badge 
                            color="success" 
                            variant="dot" 
                            invisible={!outlookApiEnabled} 
                            overlap="circular"
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <div className="icon-container" style={{ 
                                position: 'relative', 
                                cursor: 'pointer',
                                background: 'rgba(245, 245, 245, 0.8)',
                                borderRadius: '50%',
                                padding: containerPadding,
                                boxShadow: outlookApiEnabled ? '0 0 15px rgba(87, 87, 176, 0.6)' : 'none',
                                transition: 'all 0.3s ease'
                            }}>
                                <img
                                    src={settingOutlookConnectorIcon}
                                    alt="Outlook API Connector Status"
                                    style={{ 
                                        width: iconSize, 
                                        height: iconSize, 
                                        filter: outlookApiEnabled ? 'none' : 'grayscale(100%)',
                                        transition: 'all 0.3s ease',
                                        transform: outlookApiEnabled ? 'scale(1.05)' : 'scale(1)'
                                    }}
                                    onClick={handleOpen}
                                />
                                {loading && (
                                    <CircularProgress 
                                        size={progressSize} 
                                        style={{ 
                                            position: 'absolute', 
                                            top: progressOffset, 
                                            left: progressOffset, 
                                            color: themeColors.primary 
                                        }} 
                                    />
                                )}
                            </div>
                        </Badge>
                    </Tooltip>
                    <Typography 
                        variant="caption"
                        sx={{ 
                            mt: 1, 
                            fontSize: captionFontSize,
                            textAlign: 'center',
                            color: outlookApiEnabled ? themeColors.primary : 'text.secondary',
                            fontWeight: outlookApiEnabled ? 'medium' : 'normal',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {t("outlook_api_connector.caption")}
                    </Typography>
                </Box>

                <Dialog 
                    open={open} 
                    onClose={handleClose} 
                    maxWidth="md" 
                    fullWidth
                    TransitionComponent={Fade}
                    TransitionProps={{ timeout: 400 }}
                    PaperProps={{
                        elevation: 5,
                        sx: {
                            borderRadius: 2,
                            background: `linear-gradient(to right bottom, ${themeColors.cardBackground}, ${themeColors.background})`,
                            overflow: 'hidden'
                        }
                    }}
                >
                    <DialogTitle 
                        sx={{ 
                            background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryDark} 100%)`,
                            color: themeColors.lightText,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <EmailIcon sx={{ mr: 1 }} />
                        {t("outlook_api_connector.title")}
                        <CloseButton
                            ariaLabel="close"
                            onClick={handleClose}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: themeColors.lightText
                            }}
                        />
                    </DialogTitle>

                    <DialogContent sx={{ p: 3 }}>
                        <StyledPaper condensed withBlur>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <SettingsIcon sx={{ color: themeColors.primary, mr: 1.5 }} />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={outlookApiEnabled}
                                                onChange={(e) => handleOutlookApiToggle(e.target.checked)}
                                                color="primary"
                                                inputProps={{ 'aria-label': 'Outlook API Connector Toggle' }}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: themeColors.primary,
                                                        '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.08)' }
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: themeColors.primaryLight
                                                    }
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ fontWeight: 'medium', color: themeColors.text }}>
                                                {t("outlook_api_connector.enable_connector")}
                                            </Typography>
                                        }
                                    />
                                </Box>
                                <Tooltip title={t("outlook_api_connector.enable_connector_tooltip")} arrow>
                                    <IconButton sx={{ color: themeColors.primary }}>
                                        <InfoIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </StyledPaper>

                        {outlookApiEnabled && (
                            <Grow in={outlookApiEnabled} timeout={500}>
                                <div>
                                    <StyledPaper withBorder>
                                        <StyledTextField
                                            label={t("outlook_api_connector.email_label")}
                                            value={account}
                                            onChange={handleFieldChange(setAccount)}
                                            disabled={!outlookApiEnabled}
                                        />
                                        
                                        {!isEmailVerified && (
                                            <Box sx={{ mb: 3, textAlign: 'center' }}>
                                                <ActionButton
                                                    variant="contained"
                                                    icon={<EmailIcon />}
                                                    label={t("outlook_api_connector.verify_email")}
                                                    onClick={handleVerifyEmail}
                                                    loading={loading && !saveSuccess}
                                                    disabled={!account || account.trim() === ''}
                                                />
                                            </Box>
                                        )}
                                        
                                        {isEmailVerified && (
                                            <>
                                                <StyledTextField
                                                    label={t("outlook_api_connector.emails_label")}
                                                    value={emails}
                                                    onChange={handleFieldChange(setEmails)}
                                                    disabled={!outlookApiEnabled}
                                                />

                                                <StyledTextField
                                                    label={t("outlook_api_connector.folders_label")}
                                                    value={folders}
                                                    onChange={handleFieldChange(setFolders)}
                                                    disabled={!outlookApiEnabled}
                                                />
                                                
                                                <StyledTextField
                                                    label={t("outlook_api_connector.search_terms_label")}
                                                    value={searchTerms}
                                                    onChange={handleFieldChange(setSearchTerms)}
                                                    disabled={!outlookApiEnabled}
                                                />
                                                
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <DateTimePicker
                                                        label={t("outlook_api_connector.last_email_found_label")}
                                                        value={outlookLastUpdate}
                                                        onChange={(newValue) => {
                                                            setOutlookLastUpdate(newValue);
                                                            setIsSaveDisabled(false);
                                                        }}
                                                        slots={{ 
                                                            textField: (props) => (
                                                                <TextField 
                                                                    {...props} 
                                                                    fullWidth 
                                                                    disabled={!outlookApiEnabled} 
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            '&.Mui-focused fieldset': {
                                                                                borderColor: themeColors.primary,
                                                                            },
                                                                        },
                                                                        '& label.Mui-focused': {
                                                                            color: themeColors.primary,
                                                                        }
                                                                    }}
                                                                />
                                                            )
                                                        }}
                                                    />
                                                    <Tooltip title={t("outlook_api_connector.last_email_tooltip")} arrow placement="top">
                                                        <IconButton sx={{ ml: 1, color: themeColors.primary }}>
                                                            <HelpOutlineIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </>
                                        )}
                                    </StyledPaper>
                                </div>
                            </Grow>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ 
                        backgroundColor: 'rgba(245, 245, 245, 0.5)',
                        backdropFilter: 'blur(5px)',
                        borderTop: '1px solid rgba(200, 200, 200, 0.3)',
                        px: 3,
                        py: 2
                    }}>
                        {outlookApiEnabled && (
                            <>
                                <ActionButton
                                    variant="outlined"
                                    icon={<CloseIcon />}
                                    label={t('close')}
                                    onClick={handleClose}
                                />
                                {isEmailVerified && (
                                    <ActionButton
                                        variant="contained"
                                        icon={<SaveIcon />}
                                        label={t("outlook_api_connector.save_button")}
                                        onClick={handleSaveOutlookApiSettings}
                                        disabled={isSaveDisabled}
                                        loading={loading && !saveSuccess}
                                        success={saveSuccess ? t("outlook_api_connector.settings_saved_success") : null}
                                        sx={{ ml: 2 }}
                                    />
                                )}
                            </>
                        )}
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
}

export default OutlookApiConnector;
